import { Component, inject, input } from '@angular/core';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwBadge } from '../../ui/badge/uw-badge';
import { UwAiTraceLink } from '../ai-trace-link/uw-ai-trace-link';
import { EvidenceStore } from '../../../core/stores/evidence.store';
import type { LossExperience } from '../../../core/models/evidence.model';
import { UwMoneyPipe, UwPercentPipe } from '../../pipes/format.pipes';

@Component({
  selector: 'uw-loss-evidence',
  standalone: true,
  imports: [UwIcon, UwAiTraceLink, UwMoneyPipe, UwPercentPipe],
  template: `
    @if (loss(); as l) {
      <div class="space-y-3">
        <div class="grid grid-cols-3 gap-2">
          <div class="rounded-lg border border-line bg-surface p-2.5 text-center">
            <p class="typo-label">Claims (5yr)</p>
            <p class="typo-mono mt-0.5 text-[16px] font-bold text-primary">{{ l.totalClaims }}</p>
          </div>
          <div class="rounded-lg border border-line bg-surface p-2.5 text-center">
            <p class="typo-label">Open</p>
            <p class="typo-mono mt-0.5 text-[16px] font-bold text-warning">{{ l.openClaims }}</p>
          </div>
          <div class="rounded-lg border border-line bg-surface p-2.5 text-center">
            <p class="typo-label">Incurred</p>
            <p class="typo-mono mt-0.5 text-[16px] font-bold text-danger">{{ l.totalIncurred | uwMoney }}</p>
          </div>
        </div>

        <p class="rounded-lg bg-surface-sunken px-3 py-2.5 text-[11.5px] leading-relaxed text-secondary">{{ l.narrative }}</p>

        @if (l.outlier; as outlier) {
          <div class="flex items-start gap-2.5 rounded-lg border border-danger/30 bg-danger-soft/50 p-3">
            <uw-icon name="alert-triangle" [size]="15" class="mt-0.5 shrink-0 text-danger" />
            <div>
              <p class="text-[12px] font-semibold text-primary">
                {{ outlier.year }} · {{ outlier.type }} · {{ outlier.amount | uwMoney }}
              </p>
              <p class="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-text-muted">
                {{ outlier.note }}
                <uw-ai-trace-link sourceId="src-loss-2023" sourceName="Sentinel_Loss_Runs.pdf" />
              </p>
            </div>
          </div>
        }

        <div class="overflow-hidden rounded-lg border border-line">
          <table class="w-full border-collapse text-[11px]">
            <thead>
              <tr class="bg-surface-sunken/60">
                <th class="typo-label px-3 py-2 text-left">Year</th>
                <th class="typo-label px-3 py-2 text-left">Carrier</th>
                <th class="typo-label px-3 py-2 text-right">Premium</th>
                <th class="typo-label px-3 py-2 text-right">Incurred</th>
                <th class="typo-label px-3 py-2 text-center">Open</th>
              </tr>
            </thead>
            <tbody>
              @for (row of l.rows; track row.policyYear) {
                <tr class="border-t border-line">
                  <td class="px-3 py-2 typo-mono text-secondary">{{ row.policyYear }}</td>
                  <td class="px-3 py-2 text-secondary">{{ row.carrier }}</td>
                  <td class="px-3 py-2 text-right typo-mono text-secondary">{{ row.premium | uwMoney }}</td>
                  <td class="px-3 py-2 text-right">
                    <span class="typo-mono font-semibold" [class]="row.incurred > 0 ? 'text-danger' : 'text-success'">
                      {{ row.incurred > 0 ? (row.incurred | uwMoney) : '—' }}
                    </span>
                  </td>
                  <td class="px-3 py-2 text-center">
                    <span class="typo-mono" [class.text-warning]="row.openClaims > 0">{{ row.openClaims }}</span>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <p class="flex items-center gap-1.5 text-[10.5px] text-text-muted">
          <uw-icon name="file-text" [size]="12" class="text-ai" />
          Loss ratio: <span class="font-semibold">{{ l.ratio | uwPercent }}</span> · Source: carrier loss runs
        </p>
      </div>
    }
  `,
})
export class UwLossEvidence {
  readonly loss = input<LossExperience | null>(null);

  private readonly evidence = inject(EvidenceStore);

  protected openSource(): void {
    this.evidence.openSource('src-loss-2023');
  }
}
